import { createSelector } from '@reduxjs/toolkit';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { CopyIcon } from '@rsos/assets/icons';
import { Text } from '@rsos/base-components';
import { INTERNATIONAL_SUPPORT } from '@rsos/capstone/src/constants/capabilities';
import { gspx } from '@rsos/gainsight';
import useAdditionalData from '@rsos/hooks/useAdditionalData';
import { selectUserPermissionsByApp } from '@rsos/sinatra';
import copyToClipboard from '@rsos/utils/copyToClipboard';
import { getMostRecentLocations } from '@rsos/utils/getMostRecentLoc';
import { handleClipboardData } from '@rsos/utils/handleClipboardData';
import { WHAT_3_WORDS } from '../../constants/permissions';
import { isExtraInfoOrMeta } from '../JurisdictionView/helpers';
import { CopyButton } from './CopyAllButton.styles';
import {
  getCallerLocationData,
  getCopyableAdditionalData,
} from './copyAllHelpers';

// Memoized selector for international support capability
const selectIsInternational = createSelector(
  [state => state.psaps.currentPSAP.activeCapabilities],
  activeCapabilities =>
    !!(activeCapabilities && activeCapabilities[INTERNATIONAL_SUPPORT]),
);

// Memoized selector for user permissions by app
const selectUserPermissions = createSelector(
  [state => state.sinatra.user],
  user => selectUserPermissionsByApp(user, 'capstone'),
);

// Memoized selector for emergency calls state
const selectEmergencyCalls = state => state.emergencyCalls;

// Memoized selector for IRP version
const selectIrpVersion = state => state.sinatra.irp.irpVersion;

// Memoized selector for what3words data
const selectWhat3Words = createSelector(
  [
    state => state.what3words.calls,
    (_, selectedCallerID) => selectedCallerID,
    (_state, _selectedCallerID, location) => location,
  ],
  (calls, selectedCallerID, location) => {
    if (!calls || !selectedCallerID || !location) return null;
    return calls[selectedCallerID]?.[
      `${location?.latitude}_${location?.longitude}`
    ]?.words;
  },
);

export const CopyAllButton = ({ isLabelHidden = false }) => {
  const permissions = useSelector(selectUserPermissions);

  const hasWhat3wordsAccess = permissions?.includes(WHAT_3_WORDS);

  const isInternational = useSelector(selectIsInternational);

  const emergencyCalls = useSelector(selectEmergencyCalls);

  const selectedCallerID = emergencyCalls.selectedCallerID;
  const selectedCall = emergencyCalls.callQueue[selectedCallerID];
  const additionalData = useAdditionalData(selectedCallerID);

  const { mostRecentLocation, mostRecentSecondaryLocation } =
    getMostRecentLocations(selectedCall);

  const location = mostRecentLocation || mostRecentSecondaryLocation;

  const what3words = useSelector(state =>
    selectWhat3Words(state, selectedCallerID, location),
  );

  const modifiedWhat3Words = hasWhat3wordsAccess ? what3words : null;
  const irpVersion = useSelector(selectIrpVersion);

  // Outbound SMS potentially doesn't have location
  const locationData = location
    ? getCallerLocationData(location, isInternational, modifiedWhat3Words)
    : '';

  const [isActive, setIsActive] = useState(false);

  const handleReset = () => {
    setIsActive(false);
  };

  const handleClick = () => {
    const { payloadID, requestID } = isExtraInfoOrMeta(mostRecentLocation);
    gspx.trackCustomEvent('Data Location', {
      irpVersion,
      name: `Copy Lat and Long`,
      Category: 'Location Card Copy coordinates',
      'Launched date': new Date(),
      payloadID,
      requestID,
    });

    const clipboardLocationData = handleClipboardData(locationData);
    const clipboardAdditionalData = getCopyableAdditionalData(additionalData);
    const fullClipboardText = clipboardAdditionalData
      ? `${clipboardLocationData}\n${clipboardAdditionalData}`
      : clipboardLocationData;

    copyToClipboard(fullClipboardText);

    setIsActive(true);

    setTimeout(() => {
      handleReset();
    }, 3000);
  };

  useEffect(() => {
    handleReset();
  }, [selectedCallerID]);

  return (
    <CopyButton
      isActive={isActive}
      onClick={handleClick}
      isLabelHidden={isLabelHidden}
      title={isLabelHidden ? 'Copy All' : null}
    >
      <CopyIcon />
      {!isLabelHidden && (
        <Text ml={'5px'} size="xsmall" lineHeight="normal">
          {isActive ? 'Copied' : 'Copy All'}
        </Text>
      )}
    </CopyButton>
  );
};

export default CopyAllButton;
